import { useContext, useEffect, useMemo, useState } from "react";
import { useLazyQuery, useQuery } from "@apollo/client";
import { PathContext, usePath } from "@/contexts/path/path";
import {
  GET_ALL_CITIES,
  GET_ALL_DIRECTIONS,
  GET_ALL_DISTRICTS,
} from "@/graphql/queries/regions";
import {
  GET_LISTING_ALERT_BOX,
  GET_LISTING_FULL_DETAILS,
  GET_MOT_NUMBER,
  // GET_SOV_LISTINGS,
} from "@/graphql/queries/listing";
import {
  FIND_LISTINGS,
  FIND_LISTINGS_WITH_SOV,
  GET_LISTING_VIEWS,
} from "@/graphql/queries/find.listings";
import { useAuthState, useIsOwner } from "@/contexts/auth/auth.provider";
import dayjs from "dayjs";
import "dayjs/locale/ar"; // use loaded locale globally
import relativeTime from "dayjs/plugin/relativeTime";
import { useAppState } from "@/contexts/app/app.provider";
import { GET_LISTING_EXTENDED_DETAILS } from "../../graphql/queries/listing";
import { getMaintainedParam } from "@/lib/apolloClient";

dayjs.locale("ar");
dayjs.extend(relativeTime);
const ADVERTISER_TYPE_MAP = {
  owner: "مالك",
  agent: "وكيل",
  exclusive_marketer: "مسوق",
  normal_marketer: "مسوق",
};
export default function useFindListings(
  { where, skipUpdateCount, ...variables } = {},
  onFinish,
) {
  const path = usePath();
  const {
    category,
    size = 20,
    page = 0,
    polygon,
    ssr,
    filters: { unpublished, villa_type, ...filters } = {},
    city,
    direction,
    district,
    listing,
  } = path;
  const { updateContext } = useAppState();
  const { token, user } = useAuthState();

  const maintainedParam = getMaintainedParam(user?.cooperative);

  const {
    data: {
      Web: {
        sov,
        find: { listings: l, total } = { listings: [], total: 0 },
      } = {},
    } = {},
    loading,
    fetchMore,
  } = useQuery(
    category?.id && (city?.id || polygon)
      ? FIND_LISTINGS_WITH_SOV
      : FIND_LISTINGS,
    {
      onCompleted: () => {
        if (!skipUpdateCount) updateContext({ listings: { total } });

        onFinish && onFinish();
      },

      skip: listing && `${page}` === "0",
      //fetchPolicy: !ssr ? "network-only" : undefined,
      variables: {
        size,
        from: ((page || 1) - 1) * size,
        sort: {
          create_time: "desc",
          has_img: "desc",
        },
        polygon,
        sov:
          category?.id && city?.id
            ? {
                listing_category: category?.id,
                city_id: city?.id,
                district_id: district?.id,
                direction_id: direction?.id,
                enabled: true,
                campaign_category: "PROMOTED",
              }
            : undefined,
        where: {
          ...(category?.id
            ? {
                category: {
                  eq: category.id,
                },
              }
            : {}),
          ...(city && !polygon
            ? {
                city_id: {
                  eq: city.id,
                },
              }
            : {}),
          ...(direction && !polygon
            ? {
                direction_id: {
                  eq: direction.id,
                },
              }
            : {}),
          ...(district && !polygon
            ? {
                district_id: {
                  eq: district.id,
                },
              }
            : {}),
          ...(`${unpublished?.eq}` === "1" ? { published: { eq: 0 } } : {}),

          ...filters,
          ...(filters?.price_12_payments?.gte > 1
            ? {
                price_12_payments: { gte: filters.price_12_payments.gte * 12 },
              }
            : {}),
          ...(villa_type?.eq && { villa_type: villa_type.eq }),
          ...where,
          ...maintainedParam,
        },

        ...(variables || {}),
      },
    },
  );

  const listings = useMemo(() => {
    if (!sov?.find?.length) return l;
    return l.filter((lx) => !sov?.find?.length && !lx.sov_campaign_id);
  }, [l, sov]);

  return [listings, { sov, loading, total, fetchMore }];
}
/*export function useFindSpecialListings() {
  const { category, city, district } = useContext(PathContext);
  const { data: { Web: { find = {} } = {} } = {}, loading } = useQuery(
    GET_SOV_LISTINGS,
    {
      skip: !category || !city,
      variables: {
        category: category?.id,
        city_id: city?.id,
        district_id: district?.id,
      },
    },
  );

  return [
    find?.listings || [],
    {
      loading,
      total: find?.total || 0,
    },
  ];
}*/

export function useFindListingsFrozenFilter(ctx) {
  const currentCtx = usePath();
  const [frozenCtx] = useState(ctx);
  const [getListings, { loading, fetchMore }] = useLazyQuery(FIND_LISTINGS);

  const [frozenListings, setListings] = useState([]);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    if (!frozenCtx) return;
    const {
      category,
      size = 20,
      page = 0,
      polygon,
      filters,
      city,
      direction,
      daily_renting_filter,
      district,
      listing,
    } = frozenCtx;
    if (page && page === currentCtx.page) return;
    if (listing) return;
    getListings({
      variables: {
        size,
        from: currentCtx.page * size,
        sort: {
          create_time: "desc",
          has_img: "desc",
        },
        polygon,
        daily_renting_filter,
        where: {
          ...(category?.id
            ? {
                category: {
                  eq: category.id,
                },
              }
            : {}),
          ...(city
            ? {
                city_id: {
                  eq: city.id,
                },
              }
            : {}),
          ...(direction
            ? {
                direction_id: {
                  eq: direction.id,
                },
              }
            : {}),
          ...(district
            ? {
                district_id: {
                  eq: district.id,
                },
              }
            : {}),
          ...(`${filters?.unpublished?.eq}` === "1"
            ? { published: { eq: 0 } }
            : {}),
          ...filters,

          unpublished: undefined,
        },
      },
    }).then(({ data }) => {
      setListings(frozenListings.concat(...(data?.Web?.find?.listings || [])));
      setTotal(data?.Web?.find?.total || 0);
    });
  }, [currentCtx.page, frozenCtx]);
  return [
    frozenListings,
    {
      loading,
      total,
      fetchMore: (from) => {
        fetchMore({ variables: { from } }).then(({ data }) => {
          setListings(
            frozenListings.concat(...(data?.Web?.find?.listings || [])),
          );
          setTotal(data?.Web?.find?.total || 0);
        });
      },
    },
  ];
}

export function useFindDailyListings(variables = {}, onFinish) {
  const { updateContext } = useAppState();
  const {
    category,
    daily_renting_filter,
    size = 20,
    page = 0,
    polygon,
    filters,
    city,
    direction,
    district,
  } = usePath();
  const {
    data: {
      Web: {
        sov,
        find: { listings: l, total } = { listings: [], total: 0 },
      } = {},
    } = {},
    loading,
    fetchMore,
  } = useQuery(
    category?.id && (city?.id || polygon)
      ? FIND_LISTINGS_WITH_SOV
      : FIND_LISTINGS,
    {
      onCompleted: () => {
        onFinish && onFinish();
        updateContext({ listings: { total } });
      },
      variables: {
        size,
        from: ((page || 1) - 1) * size,
        sort: {
          create_time: "desc",
          has_img: "desc",
        },
        polygon,
        sov:
          category?.id && city?.id
            ? {
                listing_category: category?.id,
                city_id: city?.id,
                district_id: district?.id,
                direction_id: direction?.id,
                enabled: true,
                campaign_category: "PROMOTED",
              }
            : undefined,
        where: {
          ...(category?.id
            ? {
                category: {
                  eq: category.id,
                },
              }
            : {}),
          ...(city && !polygon
            ? {
                city_id: {
                  eq: city.id,
                },
              }
            : {}),
          ...(direction && !polygon
            ? {
                direction_id: {
                  eq: direction.id,
                },
              }
            : {}),
          ...(district && !polygon
            ? {
                district_id: {
                  eq: district.id,
                },
              }
            : {}),
          published: { eq: 1 },
          ...filters,
          price:undefined,
          unpublished: undefined,
        },
        daily_renting_filter: {
          ...daily_renting_filter,
          range_price: filters.price,
        },
        ...(variables || {}),
      },
    },
  );
  const listings = useMemo(
    () =>
      l
        .filter((lx) => !sov?.find?.length && !lx.sov_campaign_id)
        .map((l) => ({
          ...l,
          range_price: daily_renting_filter?.day_date ? l.range_price : 0,
          original_range_price: daily_renting_filter?.day_date
            ? l.original_range_price
            : 0,
        })),
    [l, sov],
  );
  return [listings, { loading, total, fetchMore, sov }];
}

export function useFindListing() {
  const { listing } = usePath();
  const { token, user } = useAuthState();
  const isOwner = useIsOwner(listing);
  const { data: viewsData } = useQuery(GET_LISTING_VIEWS, {
    skip: !listing || listing?.views === "...",
    variables: {
      id: listing?.id,
    },
  });

  const { data: fullData, loading: fullLoading } = useQuery(
    GET_LISTING_FULL_DETAILS,
    {
      skip: !listing || !token || !isOwner,
      context: { token },
      variables: {
        id: listing.id,
      },
    },
  );

  const { new_listing_space_fees, active_monthly_listing_space_info, ...full } =
    {
      ...fullData?.Listing?.getFullDetails,
    };
  const spread = {
    ...full,
    ...(fullData?.Listing?.getMyListings?.listings?.[0] || {}),
  };
  const { data: alertData } = useQuery(GET_LISTING_ALERT_BOX, {
    skip:
      !listing ||
      listing?.alert_box ||
      fullLoading ||
      new_listing_space_fees?.alert_box,
    variables: {
      id: listing?.id,
    },
  });

  const { data: extendedData } = useQuery(GET_LISTING_EXTENDED_DETAILS, {
    skip: !listing,
    variables: {
      id: listing?.id,
    },
  });

  return [
    {
      ...spread,
      full: {
        new_listing_space_fees,
        active_monthly_listing_space_info,
      },
      ...listing,
      fullLoading,
      alert_box: fullLoading
        ? null
        : new_listing_space_fees?.alert_box ||
          alertData?.Web?.listing?.alert_box ||
          listing?.alert_box,
      plan_parcel_no: [listing.plan_no, listing.parcel_no]
        .filter(Boolean)
        .join(" - "),
      views: listing.views || `${viewsData?.Listing?.views}`,
      updateAtFormatted: dayjs(listing.last_update * 1000)?.fromNow(),
      create_time: dayjs(listing.create_time * 1000)?.format("DD/MM/YYYY"),
      extended_details: extendedData?.Listing?.getListingExtendedDetails,
    },
  ];
}
export function useFindDailyListing() {
  const [listing] = useFindListing();
  const { daily_renting_filter } = usePath(PathContext);
  const { data } = useQuery(GET_MOT_NUMBER, {
    skip: !listing,
    variables: {
      id: listing?.id,
    },
  });
  return [
    {
      mot_license_number: data?.Web?.listing?.mot_license_number,
      ...listing,

      ga_start_date: daily_renting_filter?.day_date?.gte
        ? dayjs(daily_renting_filter?.day_date?.gte)?.format("DD/MM/YYYY")
        : undefined,
      ga_end_date: daily_renting_filter?.day_date?.lte
        ? dayjs(daily_renting_filter?.day_date?.lte)?.format("DD/MM/YYYY")
        : undefined,
    },
  ];
}

export function useMapListing(_listing) {
  const [listing, set] = useState(_listing);

  useEffect(() => set(_listing), [_listing]);
  useQuery(GET_LISTING_VIEWS, {
    skip: !listing || listing?.views === "...",
    variables: {
      id: listing?.id,
    },
    onCompleted: (data) => {
      set({ ...listing, views: `${data?.Listing?.views}` });
    },
  });

  useQuery(GET_LISTING_ALERT_BOX, {
    skip: !listing || listing?.published || listing?.alert_box,
    variables: {
      id: listing?.id,
    },
    onCompleted: (data) => {
      set({ ...listing, alert_box: data?.Web?.listing.alert_box });
    },
  });

  return [
    {
      ...listing,
      updateAtFormatted: dayjs(listing.last_update * 1000)?.fromNow(),
      create_time: dayjs(listing.create_time * 1000)?.format("DD/MM/YYYY"),
      advertiser_type:
        ADVERTISER_TYPE_MAP[listing?.advertiser_type || "normal_marketer"],
    },
  ];
}

export function useCounters() {
  const { category, city, direction } = usePath();
  const { loading: directionsLoading, data: { Web: directionsRes } = {} } =
    useQuery(GET_ALL_DIRECTIONS, {
      skip: direction || !category || !city?.directions,
      variables: {
        ...(category ? { category: category.id } : {}),
        ...(city ? { city_id: city.id } : {}),
      },
    });
  const { loading: districtsLoading, data: { Web: districtsRes } = {} } =
    useQuery(GET_ALL_DISTRICTS, {
      skip: !category || !city || (city.directions && !direction),
      variables: {
        ...(category ? { category: category.id } : {}),
        ...(city ? { city_id: city.id } : {}),
        ...(direction ? { direction_id: direction.id } : {}),
      },
    });
  const { loading: citiesLoading, data: { Web: citiesRes } = {} } = useQuery(
    GET_ALL_CITIES,
    {
      skip: !category,
      variables: {
        ...(category ? { category: category.id } : {}),
      },
    },
  );
  return [
    districtsRes?.districts ||
      directionsRes?.directions ||
      citiesRes?.cities ||
      [],
    (citiesLoading && !city && !direction) ||
      directionsLoading ||
      districtsLoading,
  ];
}
